<template>
  <AppShell title="Redigera lånelöfte">
    <div class="grid gap-3">
      <ApplicationMetadataBox v-if="hasLoadedApplication" type-label="Lånelöfte" />

      <AppCard>
        <p v-if="isLoadingApplication" class="text-subtle">Hämtar lånelöfte...</p>
        <ApplicationErrorBox v-else-if="!hasLoadedApplication" :message="error" />
        <AppValidationGroup v-else v-slot="{ validate }">
          <form class="grid gap-6" @submit.prevent="validateFor('submit', validate, () => void onSubmit())">
            <ApplicationFormSections :sections="commitmentForm.sections" :questions="questions" />

            <ApplicationErrorBox v-if="error" :message="error" :details="errorDetails" :debug="errorDebug" />

            <p v-if="success" class="success-text">{{ success }}</p>

            <div class="flex justify-end gap-3">
              <AppButton
                type="button"
                :disabled="isSubmitting || !canSaveDraft"
                large
                @click="validateFor('draft', validate, () => void onUpdateDraft())"
              >
                {{ isSubmitting && submitAction === 'draft' ? 'Sparar...' : 'Spara utkast' }}
              </AppButton>
              <AppButton type="submit" :disabled="isSubmitting" color="green" large>
                {{ isSubmitting && submitAction === 'submit' ? 'Skickar...' : 'Skicka in lånelöfte' }}
              </AppButton>
            </div>
          </form>
        </AppValidationGroup>
      </AppCard>
    </div>
  </AppShell>
</template>

<script setup lang="ts">
import { AppButton, AppCard, AppValidationGroup } from '@staff/ui'
import AppShell from '@/components/AppShell.vue'
import {
  fetchCommitmentApplicationById,
  submitCommitmentApplication,
  updateCommitmentApplication,
} from '@/services/commitment'
import ApplicationErrorBox from '../components/ApplicationErrorBox.vue'
import ApplicationFormSections from '../components/ApplicationFormSections.vue'
import ApplicationMetadataBox from '../components/ApplicationMetadataBox.vue'
import { questions } from '../questions'
import commitmentForm from '../forms/lofte'
import { buildCommitmentUpdatePayload, hydrateCommitmentForm } from '../commitmentFormMapper'
import { useApplicationFormData } from '../composables/applicationFormData'
import { useEditApplicationDraft } from '../composables/useEditApplicationDraft'

const { form, validateFor } = useApplicationFormData()

const {
  isSubmitting,
  isLoadingApplication,
  hasLoadedApplication,
  submitAction,
  error,
  errorDetails,
  errorDebug,
  success,
  canSaveDraft,
  onUpdateDraft,
  onSubmit,
} = useEditApplicationDraft({
  fetchForm: fetchCommitmentApplicationById,
  hydrate: hydrateCommitmentForm,
  update: (id) => updateCommitmentApplication(id, buildCommitmentUpdatePayload(form)),
  submit: async (id) => {
    await submitCommitmentApplication(id)
  },
  redirectTo: (id) => `/lofte/${id}`,
  messages: {
    loadFailed: 'Kunde inte hämta lånelöftet.',
    saveFailed: 'Kunde inte spara lånelöftet.',
    draftSaved: (id) => `Lånelöftet ${id} har sparats.`,
  },
})
</script>

<style scoped lang="scss">
.success-text {
  color: var(--color-success-fg);
}
</style>
